import React, { useEffect, useState } from "react";
import { collection, getDocs, doc, getDoc } from "firebase/firestore";
import { db } from "../config/firebase";
import PageTemplate from "../templates/PageTemplate";
import { Card, CardHeader, CardContent, CardTitle } from "../components/Card";
import moment from "moment";
import { useNavigate } from "react-router-dom";

const STATUS_LABELS = {
  pending: "En attente",
  confirmed: "Confirmé",
  waitPayment: "En attente paiement",
  paymentConfirmed: "Paiement confirmé",
};

const STATUS_COLORS = {
  pending: "bg-yellow-100 text-yellow-800 border-yellow-200",
  confirmed: "bg-blue-100 text-blue-800 border-blue-200",
  waitPayment: "bg-orange-100 text-orange-800 border-orange-200",
  paymentConfirmed: "bg-green-100 text-green-800 border-green-200",
};

// fonction pour tronquer texte
function truncate(text, max = 50) {
  if (!text) return "-";
  return text.length > max ? text.slice(0, max) + "…" : text;
}

export default function AppointmentsList() {
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("all");
  const navigate = useNavigate();

  useEffect(() => {
    async function fetchAppointments() {
      setLoading(true);
      const snap = await getDocs(collection(db, "appointments"));
      const data = [];

      for (const docSnap of snap.docs) {
        const appoint = docSnap.data();

        let clientEmail = "";
        let couturierEmail = "";
        let offerMessage = "";

        if (appoint.clientId) {
          const clientSnap = await getDoc(doc(db, "users", appoint.clientId));
          if (clientSnap.exists()) clientEmail = clientSnap.data().email || "";
        }
        if (appoint.couturierId) {
          const couturierSnap = await getDoc(
            doc(db, "users", appoint.couturierId)
          );
          if (couturierSnap.exists())
            couturierEmail = couturierSnap.data().email || "";
        }
        if (appoint.offerId) {
          const offerSnap = await getDoc(doc(db, "offers", appoint.offerId));
          if (offerSnap.exists()) {
            offerMessage = offerSnap.data().message || "";
          }
        }

        data.push({
          id: docSnap.id,
          clientEmail,
          couturierEmail,
          date: appoint.date,
          status: appoint.status,
          paidAt: appoint.paidAt,
          offerMessage,
          createdAt: appoint.createdAt,
        });
      }

      // Trier par date de création (plus récent en premier)
      data.sort((a, b) => {
        const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt || 0);
        const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt || 0);
        return dateB - dateA;
      });

      setAppointments(data);
      setLoading(false);
    }
    fetchAppointments();
  }, []);

  const filteredAppointments =
    filterStatus === "all"
      ? appointments
      : appointments.filter((a) => a.status === filterStatus);

  const statusCounts = {
    all: appointments.length,
    pending: appointments.filter((a) => a.status === "pending").length,
    confirmed: appointments.filter((a) => a.status === "confirmed").length,
    waitPayment: appointments.filter((a) => a.status === "waitPayment").length,
    paymentConfirmed: appointments.filter((a) => a.status === "paymentConfirmed")
      .length,
  };

  return (
    <PageTemplate
      loading={loading}
      content={
        <div className="max-w-7xl mx-auto space-y-6">
          {/* Info Card */}
          <Card className="bg-gradient-to-r from-red-50 to-rose-50 border-red-200">
            <CardContent className="flex items-start gap-4">
              <div className="p-3 bg-[#801111] rounded-lg">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="w-6 h-6 text-white"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5"
                  />
                </svg>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">
                  Gestion des rendez-vous
                </h3>
                <p className="text-sm text-gray-600">
                  Consultez et gérez tous les rendez-vous entre clients et
                  couturiers. {appointments.length} rendez-vous au total.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <Card
              className={`cursor-pointer transition-all hover:shadow-lg ${
                filterStatus === "all"
                  ? "ring-2 ring-[#801111] bg-red-50"
                  : "hover:bg-gray-50"
              }`}
              onClick={() => setFilterStatus("all")}
            >
              <CardContent className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Total</p>
                  <p className="text-2xl font-bold text-[#801111]">
                    {statusCounts.all}
                  </p>
                </div>
                <div className="p-3 bg-red-100 rounded-full">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                    className="w-6 h-6 text-[#801111]"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5"
                    />
                  </svg>
                </div>
              </CardContent>
            </Card>

            <Card
              className={`cursor-pointer transition-all hover:shadow-lg ${
                filterStatus === "pending"
                  ? "ring-2 ring-yellow-500 bg-yellow-50"
                  : "hover:bg-gray-50"
              }`}
              onClick={() => setFilterStatus("pending")}
            >
              <CardContent className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">En attente</p>
                  <p className="text-2xl font-bold text-yellow-600">
                    {statusCounts.pending}
                  </p>
                </div>
                <div className="p-3 bg-yellow-100 rounded-full">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                    className="w-6 h-6 text-yellow-600"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
                    />
                  </svg>
                </div>
              </CardContent>
            </Card>

            <Card
              className={`cursor-pointer transition-all hover:shadow-lg ${
                filterStatus === "confirmed"
                  ? "ring-2 ring-blue-500 bg-blue-50"
                  : "hover:bg-gray-50"
              }`}
              onClick={() => setFilterStatus("confirmed")}
            >
              <CardContent className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Confirmés</p>
                  <p className="text-2xl font-bold text-blue-600">
                    {statusCounts.confirmed}
                  </p>
                </div>
                <div className="p-3 bg-blue-100 rounded-full">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                    className="w-6 h-6 text-blue-600"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
                    />
                  </svg>
                </div>
              </CardContent>
            </Card>

            <Card
              className={`cursor-pointer transition-all hover:shadow-lg ${
                filterStatus === "waitPayment"
                  ? "ring-2 ring-orange-500 bg-orange-50"
                  : "hover:bg-gray-50"
              }`}
              onClick={() => setFilterStatus("waitPayment")}
            >
              <CardContent className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">
                    Att. paiement
                  </p>
                  <p className="text-2xl font-bold text-orange-600">
                    {statusCounts.waitPayment}
                  </p>
                </div>
                <div className="p-3 bg-orange-100 rounded-full">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                    className="w-6 h-6 text-orange-600"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M2.25 18.75a60.07 60.07 0 0 1 15.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 0 1 3 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 0 0-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 0 1-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 0 0 3 15h-.75M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Zm3 0h.008v.008H18V10.5Zm-12 0h.008v.008H6V10.5Z"
                    />
                  </svg>
                </div>
              </CardContent>
            </Card>

            <Card
              className={`cursor-pointer transition-all hover:shadow-lg ${
                filterStatus === "paymentConfirmed"
                  ? "ring-2 ring-green-500 bg-green-50"
                  : "hover:bg-gray-50"
              }`}
              onClick={() => setFilterStatus("paymentConfirmed")}
            >
              <CardContent className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Payés</p>
                  <p className="text-2xl font-bold text-green-600">
                    {statusCounts.paymentConfirmed}
                  </p>
                </div>
                <div className="p-3 bg-green-100 rounded-full">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                    className="w-6 h-6 text-green-600"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M9 12.75 11.25 15 15 9.75M21 12c0 1.268-.63 2.39-1.593 3.068a3.745 3.745 0 0 1-1.043 3.296 3.745 3.745 0 0 1-3.296 1.043A3.745 3.745 0 0 1 12 21c-1.268 0-2.39-.63-3.068-1.593a3.746 3.746 0 0 1-3.296-1.043 3.745 3.745 0 0 1-1.043-3.296A3.745 3.745 0 0 1 3 12c0-1.268.63-2.39 1.593-3.068a3.745 3.745 0 0 1 1.043-3.296 3.746 3.746 0 0 1 3.296-1.043A3.746 3.746 0 0 1 12 3c1.268 0 2.39.63 3.068 1.593a3.746 3.746 0 0 1 3.296 1.043 3.746 3.746 0 0 1 1.043 3.296A3.745 3.745 0 0 1 21 12Z"
                    />
                  </svg>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Appointments Table */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>
                  {filterStatus === "all"
                    ? "Tous les rendez-vous"
                    : `${STATUS_LABELS[filterStatus]} (${statusCounts[filterStatus]})`}
                </CardTitle>
                {filterStatus !== "all" && (
                  <button
                    onClick={() => setFilterStatus("all")}
                    className="text-sm text-[#801111] hover:text-[#5a0c0c] font-medium flex items-center gap-1"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={1.5}
                      stroke="currentColor"
                      className="w-4 h-4"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M6 18 18 6M6 6l12 12"
                      />
                    </svg>
                    Réinitialiser le filtre
                  </button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {filteredAppointments.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={1.5}
                      stroke="currentColor"
                      className="w-8 h-8 text-gray-400"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5"
                      />
                    </svg>
                  </div>
                  <p className="text-gray-600 font-medium mb-1">
                    Aucun rendez-vous
                  </p>
                  <p className="text-sm text-gray-500">
                    {filterStatus === "all"
                      ? "Aucun rendez-vous n'a été créé pour le moment"
                      : `Aucun rendez-vous avec le statut "${STATUS_LABELS[filterStatus]}"`}
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full">
                    <thead className="bg-gray-50 border-b-2 border-gray-200">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                          Client
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                          Couturier
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                          Date RDV
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                          Statut
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                          Offre
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                          Créé le
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                          Action
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredAppointments.map((a) => (
                        <tr key={a.id} className="hover:bg-gray-50 transition-colors">
                          <td className="px-4 py-4">
                            {a.clientEmail || (
                              <span className="text-xs italic text-red-600 bg-red-50 px-2 py-1 rounded">
                                Utilisateur inexistant
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-4">
                            {a.couturierEmail || (
                              <span className="text-xs italic text-red-600 bg-red-50 px-2 py-1 rounded">
                                Utilisateur inexistant
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                strokeWidth={1.5}
                                stroke="currentColor"
                                className="w-4 h-4 text-gray-400"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5"
                                />
                              </svg>
                              <span className="text-sm font-medium">
                                {a.date
                                  ? moment(a.date).format("DD/MM/YYYY HH:mm")
                                  : "-"}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-4">
                            <span
                              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${
                                STATUS_COLORS[a.status] ||
                                "bg-gray-100 text-gray-800 border-gray-200"
                              }`}
                            >
                              {STATUS_LABELS[a.status] || a.status}
                            </span>
                          </td>
                          <td
                            className="px-4 py-4 max-w-xs"
                            title={a.offerMessage}
                          >
                            <span className="text-sm text-gray-600">
                              {truncate(a.offerMessage, 40)}
                            </span>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">
                            {a.createdAt && a.createdAt.toDate
                              ? moment(a.createdAt.toDate()).format(
                                  "DD/MM/YYYY HH:mm"
                                )
                              : a.createdAt
                              ? moment(a.createdAt).format("DD/MM/YYYY HH:mm")
                              : "-"}
                          </td>
                          <td className="px-4 py-4">
                            <button
                              onClick={() => navigate(`/rdv/${a.id}`)}
                              className="inline-flex items-center gap-1 text-[#801111] hover:text-[#5a0c0c] font-medium text-sm transition-colors"
                              type="button"
                              aria-label="Voir détails rendez-vous"
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                strokeWidth={1.5}
                                stroke="currentColor"
                                className="w-4 h-4"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z"
                                />
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
                                />
                              </svg>
                              Voir
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      }
    />
  );
}